import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FFmpegService } from '../ffmpeg-service';
import { CloudUpload, CheckCircle, AlertCircle, Loader2, FileVideo, Trash2, Download, Expand, Shrink, Pencil, Search, Replace, Plus, Clock, XCircle, Server } from 'lucide-react';
import {
    getR2UploadUrl,
    transcribeAudioConstance,
    callGeminiConstance,
    cancelReplicateJob
} from '../services/firebase-api';
import { uploadFileToR2 } from '../services/upload-manager';
import { saveProjectToFirestore } from '../firestore';
import { auth, firestore } from '../firebase';
import type { SavedProject, Clip, Track } from '../types';
import { fixSubtitleOverlaps } from '../utils/subtitle-helpers';
import { getServerExportClient } from '../server-export-client';

// ─── Types ───────────────────────────────────────────────────────
type BatchStatus =
    | 'queued'
    | 'extracting'
    | 'uploading-video'
    | 'uploading-audio'
    | 'transcribing'
    | 'analyzing'
    | 'creating-project'
    | 'ready-export'
    | 'exporting'
    | 'done'
    | 'error';

interface SubtitleSegment {
    text: string;
    start: number;
    end: number;
}

const normalizeSubtitleSegments = (segments: SubtitleSegment[]): SubtitleSegment[] => {
    const withIds = segments.map((seg, index) => ({
        id: index,
        text: seg.text,
        start: seg.start,
        end: seg.end,
    }));
    const fixed = fixSubtitleOverlaps(withIds);
    return fixed.map(({ text, start, end }) => ({ text, start, end }));
};

const extractFinalExportUrl = (data: any): string | null => {
    const direct =
        data?.downloadUrl ||
        data?.outputUrl ||
        data?.output_url ||
        data?.url ||
        data?.resultUrl ||
        data?.videoUrl ||
        null;
    if (typeof direct === 'string' && direct.startsWith('http')) return direct;

    // Fallback: single chunk export sometimes only exposes chunk_0_url
    if ((data?.totalChunks || 0) === 1) {
        const chunk0 = data?.chunk_0_url;
        if (typeof chunk0 === 'string' && chunk0.startsWith('http')) return chunk0;
    }

    return null;
};

interface BatchItem {
    id: string;
    file: File;
    status: BatchStatus;
    progress: number;
    r2VideoUrl: string | null;
    r2AudioUrl: string | null;
    subtitles: SubtitleSegment[] | null;
    analysis: string | null;
    projectId: string | null;   // Firestore project ID
    exportUrl: string | null;   // Final exported video with burned subtitles
    error: string | null;
    logs: string[];
    localPreviewUrl: string;
}

const STATUS_LABELS: Record<BatchStatus, string> = {
    'queued': '⏳ En attente',
    'extracting': '🎧 Extraction audio…',
    'uploading-video': '☁️ Upload vidéo…',
    'uploading-audio': '☁️ Upload audio…',
    'transcribing': '🧠 Transcription…',
    'analyzing': '💎 Analyse Gemini…',
    'creating-project': '📁 Création projet…',
    'ready-export': '✍️ Valider sous-titres',
    'exporting': '🔥 Export sous-titres…',
    'done': '✅ Terminé',
    'error': '❌ Erreur',
};

const EXPORT_SERVICE_URL = import.meta.env.VITE_EXPORT_SERVICE_URL || 'https://export-service-400620783493.us-central1.run.app';

// ─── Default tracks matching App.tsx ──────────────────────────
const DEFAULT_TRACKS: Track[] = [
    { id: 't1', label: 'Overlays', accepts: ['text', 'image', 'synthe', 'panel'], isMagnetic: false, isVisible: true, isMuted: true },
    { id: 's1', label: 'Split', accepts: ['video', 'image', 'text'], isMagnetic: false, isVisible: true, isMuted: false },
    { id: 'v1', label: '1', accepts: ['video', 'image', 'audio', 'text'], isMagnetic: false, isVisible: true, isMuted: false },
    { id: 'v2', label: '2', accepts: ['video', 'image', 'audio', 'text'], isMagnetic: false, isVisible: true, isMuted: false },
    { id: 'z1', label: 'Zoom', accepts: ['zoom'], isMagnetic: false, isVisible: true, isMuted: true },
];

// ─── Helper: Generate SRT from subtitle segments ─────────────────
function generateSrt(segments: { text: string; start: number; end: number }[]): string {
    const fmtTime = (s: number) => {
        const h = Math.floor(s / 3600).toString().padStart(2, '0');
        const m = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
        const sec = Math.floor(s % 60).toString().padStart(2, '0');
        const ms = Math.round(((s % 1) * 1000)).toString().padStart(3, '0');
        return `${h}:${m}:${sec},${ms}`;
    };
    return segments.map((seg, i) =>
        `${i + 1}\n${fmtTime(seg.start)} --> ${fmtTime(seg.end)}\n${seg.text}\n`
    ).join('\n');
}

// ─── Helper: uid generator ───────────────────────────────────────
const uid = () => 'id-' + Math.random().toString(36).slice(2, 8) + Date.now().toString(36);

// ─── Component ───────────────────────────────────────────────────
export const BatchPage: React.FC = () => {
    const [isFfmpegLoaded, setIsFfmpegLoaded] = useState(false);
    const [items, setItems] = useState<BatchItem[]>([]);
    const [globalLogs, setGlobalLogs] = useState<string[]>([]);
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
    const [autoExport, setAutoExport] = useState(false);
    const [showSubEditor, setShowSubEditor] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [replaceQuery, setReplaceQuery] = useState('');
    const [showReplace, setShowReplace] = useState(false);
    const [editingSubId, setEditingSubId] = useState<number | null>(null);
    const [activeSubIndex, setActiveSubIndex] = useState<number>(-1);
    const [previewTime, setPreviewTime] = useState(0);
    const [previewSeekTime, setPreviewSeekTime] = useState<number | null>(null);
    const subListRef = useRef<HTMLDivElement>(null);
    const isProcessingRef = useRef(false);
    const itemsRef = useRef<BatchItem[]>([]);
    const [adminJobs, setAdminJobs] = useState<any[]>([]);
    const [adminLock, setAdminLock] = useState<any>(null);
    const [adminActiveCount, setAdminActiveCount] = useState(0);
    const [userNames, setUserNames] = useState<Record<string, string>>({});
    const [showAdminPanel, setShowAdminPanel] = useState(true);
    const [managedJobId, setManagedJobId] = useState<string | null>(null);
    const [managedChunks, setManagedChunks] = useState<{ index: number; status: string; progress: number; url: string | null; error?: string }[]>([]);
    const [managedJobData, setManagedJobData] = useState<any>(null);
    const [isConcatting, setIsConcatting] = useState(false);
    const [concatProgress, setConcatProgress] = useState('');
    const [concatResultUrl, setConcatResultUrl] = useState<string | null>(null);
    const [concatFileName, setConcatFileName] = useState('');

    useEffect(() => { itemsRef.current = items; }, [items]);
    useEffect(() => {
        return () => {
            itemsRef.current.forEach(item => URL.revokeObjectURL(item.localPreviewUrl));
        };
    }, []);

    // ── Firestore listener for managed job chunks ──
    useEffect(() => {
        if (!managedJobId) { setManagedChunks([]); setManagedJobData(null); return; }
        let unsubscribe: (() => void) | null = null;
        (async () => {
            const { doc, onSnapshot, getFirestore } = await import('firebase/firestore');
            const db = getFirestore();
            const jobDocRef = doc(db, 'export-jobs', managedJobId);
            unsubscribe = onSnapshot(jobDocRef, (snapshot) => {
                if (!snapshot.exists()) return;
                const data = snapshot.data();
                setManagedJobData(data);
                const totalChunks = data?.totalChunks || 0;
                if (totalChunks > 0) {
                    const chunks = [];
                    for (let i = 0; i < totalChunks; i++) {
                        chunks.push({
                            index: i,
                            status: data?.[`chunk_${i}_status`] || 'pending',
                            progress: data?.[`chunk_${i}_progress`] || 0,
                            url: data?.[`chunk_${i}_url`] || null,
                            error: data?.[`chunk_${i}_error`] || undefined,
                            hasSubtitles: data?.[`chunk_${i}_hasSubtitles`] ?? null,
                        });
                    }
                    setManagedChunks(chunks);
                }
            });
        })();
        return () => { if (unsubscribe) unsubscribe(); };
    }, [managedJobId]);

    const retryFailedChunks = useCallback(async (jobId: string) => {
        try {
            // 100% Firestore — no REST call (avoids 429+CORS entirely)
            const { doc, getDoc, updateDoc, getFirestore, Timestamp } = await import('firebase/firestore');
            const db = getFirestore();
            const jobDocRef = doc(db, 'export-jobs', jobId);
            const snap = await getDoc(jobDocRef);
            if (!snap.exists()) { addGlobalLog('❌ Job not found'); return; }

            const data = snap.data();
            const totalChunks = data?.totalChunks || 0;
            const failedChunks: number[] = [];
            for (let i = 0; i < totalChunks; i++) {
                const status = data?.[`chunk_${i}_status`];
                if (['failed', 'error', 'encoding', 'downloading'].includes(status)) failedChunks.push(i);
            }

            if (failedChunks.length === 0) { addGlobalLog('✅ Aucun chunk échoué'); return; }

            // Reset failed chunks to pending + flag for server retry
            const resetUpdate: Record<string, any> = {
                status: 'processing',
                currentStep: `Relance de ${failedChunks.length} chunk(s) demandée...`,
                retryRequestedAt: Timestamp.now(),
                retryChunks: failedChunks,
            };
            for (const ci of failedChunks) {
                resetUpdate[`chunk_${ci}_status`] = 'pending';
                resetUpdate[`chunk_${ci}_progress`] = 0;
                resetUpdate[`chunk_${ci}_error`] = null;
            }
            await updateDoc(jobDocRef, resetUpdate);
            addGlobalLog(`🔄 ${failedChunks.length} chunks reset → pending. Le serveur les re-traitera automatiquement.`);
        } catch (e: any) {
            addGlobalLog(`❌ Retry failed: ${e.message}`);
        }
    }, []);

    const concatManagedChunks = useCallback(async () => {
        const chunksWithUrl = managedChunks.filter(c => c.url).sort((a, b) => a.index - b.index);
        if (chunksWithUrl.length === 0) { addGlobalLog('❌ Aucun chunk avec URL disponible'); return; }

        setIsConcatting(true);
        setConcatProgress(`Téléchargement de ${chunksWithUrl.length} chunks...`);
        addGlobalLog(`🔗 Concat: ${chunksWithUrl.length}/${managedChunks.length} chunks disponibles`);

        try {
            const ffmpeg = await FFmpegService.getInstance();

            // Download each chunk to FFmpeg FS
            for (let i = 0; i < chunksWithUrl.length; i++) {
                const chunk = chunksWithUrl[i];
                setConcatProgress(`⬇️ Chunk ${chunk.index} (${i + 1}/${chunksWithUrl.length})...`);
                const response = await fetch(chunk.url!);
                if (!response.ok) throw new Error(`Failed to download chunk ${chunk.index}: HTTP ${response.status}`);
                const buffer = await response.arrayBuffer();
                const fileName = `chunk_${String(chunk.index).padStart(3, '0')}.mp4`;
                await ffmpeg.writeFile(fileName, new Uint8Array(buffer));
                addGlobalLog(`⬇️ Chunk ${chunk.index}: ${(buffer.byteLength / 1024 / 1024).toFixed(1)} MB`);
            }

            // Create concat list (only chunks that have URLs, in order)
            setConcatProgress('🔗 Assemblage FFmpeg...');
            const concatContent = chunksWithUrl.map(c =>
                `file 'chunk_${String(c.index).padStart(3, '0')}.mp4'`
            ).join('\n');
            await ffmpeg.writeFile('concat_chunks.txt', new TextEncoder().encode(concatContent));

            // FFmpeg concat -c copy (no re-encoding)
            await ffmpeg.exec([
                '-f', 'concat', '-safe', '0',
                '-i', 'concat_chunks.txt',
                '-c', 'copy', '-movflags', '+faststart',
                '-y', 'merged_output.mp4'
            ]);

            // Read result
            const outputData = await ffmpeg.readFile('merged_output.mp4') as Uint8Array;
            const blob = new Blob([outputData.slice().buffer], { type: 'video/mp4' });
            const blobUrl = URL.createObjectURL(blob);
            const sizeMB = (blob.size / 1024 / 1024).toFixed(1);
            const fileName = `${managedJobId || 'export'}_merged.mp4`;

            // Store for preview
            setConcatResultUrl(blobUrl);
            setConcatFileName(fileName);

            addGlobalLog(`✅ Concat terminé! ${sizeMB} MB (${chunksWithUrl.length} chunks)`);
            setConcatProgress(`✅ ${sizeMB} MB`);

            // Cleanup FFmpeg FS
            for (const chunk of chunksWithUrl) {
                await ffmpeg.deleteFile(`chunk_${String(chunk.index).padStart(3, '0')}.mp4`).catch(() => { });
            }
            await ffmpeg.deleteFile('concat_chunks.txt').catch(() => { });
            await ffmpeg.deleteFile('merged_output.mp4').catch(() => { });

        } catch (err: any) {
            console.error('[Concat] Error:', err);
            addGlobalLog(`❌ Concat failed: ${err.message}`);
            setConcatProgress('');
        } finally {
            setIsConcatting(false);
        }
    }, [managedChunks, managedJobId]);

    // ── Load FFmpeg on mount ──
    useEffect(() => {
        (async () => {
            try {
                addGlobalLog('Chargement de FFmpeg…');
                await FFmpegService.getInstance();
                setIsFfmpegLoaded(true);
                addGlobalLog('FFmpeg chargé ✓');
            } catch (e: any) {
                addGlobalLog(`❌ FFmpeg: ${e.message}`);
            }
        })();
    }, []);

    const addGlobalLog = (msg: string) => {
        setGlobalLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
    };

    const updateItem = useCallback((id: string, patch: Partial<BatchItem>) => {
        setItems(prev => prev.map(item => item.id === id ? { ...item, ...patch } : item));
    }, []);

    const addItemLog = useCallback((id: string, msg: string) => {
        setItems(prev => prev.map(item =>
            item.id === id ? { ...item, logs: [...item.logs, `[${new Date().toLocaleTimeString()}] ${msg}`] } : item
        ));
    }, []);

    const exportItemWithBurnedSubtitles = useCallback(async (itemId: string) => {
        const item = itemsRef.current.find(i => i.id === itemId);
        if (!item) return;
        if (!item.subtitles || item.subtitles.length === 0) {
            addItemLog(itemId, '⚠️ Aucun sous-titre à exporter');
            return;
        }
        if (!item.r2VideoUrl || !item.projectId) {
            addItemLog(itemId, '⚠️ Export impossible: vidéo/projet manquant');
            return;
        }

        try {
            updateItem(itemId, { status: 'exporting', progress: 0, error: null });
            addItemLog(itemId, '🔥 Lancement export sous-titres brûlés…');

            const user = auth.currentUser;
            if (!user) throw new Error('Non authentifié — connectez-vous d\'abord');

            const fileName = item.file.name.replace(/\.[^/.]+$/, '');
            const srtContent = generateSrt(normalizeSubtitleSegments(item.subtitles));
            const srtBlob = new Blob([srtContent], { type: 'text/plain' });
            const srtFilename = `subtitles_batch_${Date.now()}.srt`;
            const uploadResult = await getR2UploadUrl({ fileName: srtFilename, contentType: 'text/plain' });
            const { uploadUrl, publicUrl: r2SubtitleUrl } = (uploadResult.data as any);

            await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': 'text/plain' },
                body: srtBlob,
            });
            addItemLog(itemId, '✓ SRT uploadé sur R2');

            const jobId = `export_batch_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
            const { doc, onSnapshot, getFirestore } = await import('firebase/firestore');
            const db = getFirestore();
            const jobDocRef = doc(db, 'export-jobs', jobId);

            fetch(`${EXPORT_SERVICE_URL}/burn-subtitles-parallel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    jobId,
                    videoUrl: item.r2VideoUrl,
                    subtitleUrl: r2SubtitleUrl,
                    userId: user.uid,
                    projectId: item.projectId,
                }),
            }).catch(() => { });

            addItemLog(itemId, `☁️ Job export lancé: ${jobId}`);

            await new Promise<void>((resolve, reject) => {
                const unsubscribe = onSnapshot(jobDocRef, async (snapshot) => {
                    if (!snapshot.exists()) return;
                    const data = snapshot.data();
                    const totalChunks = data?.totalChunks || 1;
                    let chunksDone = 0;
                    let chunksFailed = 0;

                    if (totalChunks > 0) {
                        for (let ci = 0; ci < totalChunks; ci++) {
                            const cStatus = data?.[`chunk_${ci}_status`] || 'pending';
                            if (cStatus === 'complete') chunksDone++;
                            else if (cStatus === 'failed') chunksFailed++;
                        }
                    }

                    const progress = totalChunks > 0
                        ? Math.round((chunksDone / totalChunks) * 100)
                        : (data?.progress || 0);
                    updateItem(itemId, { progress });

                    if (data?.status === 'complete' || data?.status === 'completed') {
                        const finalUrl = extractFinalExportUrl(data);
                        if (!finalUrl) {
                            // Some workers write status first and URL a bit later. Keep listening.
                            addItemLog(itemId, '⏳ Export terminé, attente URL finale...');
                            return;
                        }

                        unsubscribe();
                        updateItem(itemId, { exportUrl: finalUrl, status: 'done', progress: 100 });
                        addItemLog(itemId, '✅ Export terminé !');

                        if (finalUrl) {
                            try {
                                const projectRef = firestore.collection('users').doc(user.uid).collection('projects').doc(item.projectId!);
                                const projectDoc = await projectRef.get();
                                const existingExports = projectDoc.data()?.exports || [];

                                await projectRef.update({
                                    exports: [...existingExports, {
                                        id: jobId,
                                        name: `${fileName} — Sous-titres brûlés`,
                                        url: finalUrl,
                                        urlMP4: finalUrl,
                                        urlWebM: finalUrl,
                                        createdAt: Date.now(),
                                    }],
                                    updatedAt: Date.now(),
                                });
                                addItemLog(itemId, '✓ Export sauvegardé dans le projet');
                            } catch (saveErr: any) {
                                addItemLog(itemId, `⚠️ Save export: ${saveErr.message}`);
                            }
                        }

                        resolve();
                        return;
                    }

                    if (data?.status === 'failed') {
                        unsubscribe();
                        reject(new Error(data.error || 'Export failed'));
                        return;
                    }

                    if (chunksFailed > 0 && chunksDone + chunksFailed >= totalChunks) {
                        unsubscribe();
                        reject(new Error(`${chunksFailed}/${totalChunks} chunks échoués`));
                    }
                });
            });
        } catch (err: any) {
            console.error(`[Batch Export] ${item.file.name} failed:`, err);
            updateItem(itemId, { status: 'error', error: err.message });
            addItemLog(itemId, `❌ ${err.message}`);
        }
    }, [addItemLog, updateItem]);

    // ── Full Magic Workflow for ONE file ──
    const processOneFile = useCallback(async (item: BatchItem) => {
        const { id, file } = item;

        try {
            if (!isFfmpegLoaded) {
                throw new Error('FFmpeg n’est pas prêt. Attendez le statut ONLINE puis relancez.');
            }

            // ─── 1. Audio Extraction (Local & Synchronous) ───
            updateItem(id, { status: 'extracting', progress: 0 });
            addItemLog(id, '🎧 Extraction audio locale…');

            const audioBlob = await FFmpegService.extractAudioFromLargeFile(file, (ratio) => {
                updateItem(id, { progress: Math.min(Math.round(ratio * 100), 99) }); // Keep it from hitting 100% just yet
            });
            const mp3File = new File([audioBlob], 'extract.mp3', { type: 'audio/mpeg' });
            addItemLog(id, `✓ Audio extrait (${(audioBlob.size / 1024 / 1024).toFixed(1)} MB)`);

            // State variables to capture results from parallel branches
            let finalVideoUrl: string | null = null;
            let finalAudioUrl: string | null = null;
            let finalSegments: SubtitleSegment[] | null = null;
            let finalAnalysis: string | null = null;
            let finalTranscriptText: string | null = null;

            // ─── Branch A: Video Upload (Slow, runs in background) ───
            const videoUploadPromise = async () => {
                addItemLog(id, '☁️ [Vidéo] Upload vers R2 démarré…');
                const vUrl = await uploadFileToR2({
                    file,
                    type: 'video',
                    // Only update progress if the audio branch is already done to avoid UI flickering, 
                    // or just let the user know it's happening quietly. 
                    // We'll update the main progress here because it's the longest task.
                    onProgress: (p) => {
                        updateItem(id, { progress: p });
                    },
                    logFn: (msg) => { /* Avoid spamming logs for video chunks unless error */ }
                });
                finalVideoUrl = vUrl;
                updateItem(id, { r2VideoUrl: vUrl });
                addItemLog(id, `✓ [Vidéo] R2 OK`);
            };

            // ─── Branch B: Audio Upload, Transcription, and Analysis (Fast) ───
            const audioPipelinePromise = async () => {
                // Audio Upload
                updateItem(id, { status: 'uploading-audio' });
                addItemLog(id, '☁️ [Audio] Upload vers R2…');
                const aUrl = await uploadFileToR2({
                    file: mp3File,
                    type: 'audio',
                    onProgress: () => { }, // Handled by video upload
                    logFn: (msg) => addItemLog(id, msg),
                });
                finalAudioUrl = aUrl;
                updateItem(id, { r2AudioUrl: aUrl });
                addItemLog(id, `✓ [Audio] R2 OK`);

                // Transcription
                updateItem(id, { status: 'transcribing' });
                addItemLog(id, '🧠 Transcription Whisper…');

                const transcriptionJobId = `transcription_${Date.now()}_${Math.random().toString(36).substring(7)}`;

                const pollFirestoreForResult = async (): Promise<any> => {
                    const { doc, onSnapshot, getFirestore } = await import('firebase/firestore');
                    const db = getFirestore();
                    const jobRef = doc(db, 'transcription-jobs', transcriptionJobId);

                    return new Promise((resolve, reject) => {
                        const unsubscribe = onSnapshot(jobRef, (snapshot) => {
                            if (!snapshot.exists()) return;
                            const data = snapshot.data();
                            if (data?.status === 'complete') {
                                unsubscribe();
                                resolve({ data: { subtitles: data.subtitles, language: data.language } });
                            } else if (data?.status === 'error') {
                                unsubscribe();
                                reject(new Error(data.error || 'Transcription failed'));
                            }
                        });
                    });
                };

                const httpPromise = transcribeAudioConstance({
                    audioUrl: aUrl,
                    language: 'en',
                    response_format: 'verbose_json',
                    jobId: transcriptionJobId,
                }).catch(() => null);

                const transRes = await Promise.race([
                    httpPromise.then((r: any) => r ? r : pollFirestoreForResult()),
                    pollFirestoreForResult(),
                ]);
                const tData = transRes.data as any;

                let transcriptText = tData.text || tData.transcription || (tData.output && tData.output.text) || '';
                const rawSegments = tData.subtitles || tData.segments;
                const segments = Array.isArray(rawSegments)
                    ? normalizeSubtitleSegments(rawSegments.map((s: any) => ({
                        text: s.text || '',
                        start: Number(s.start) || 0,
                        end: Number(s.end) || 0,
                    })))
                    : null;
                if (!transcriptText && segments) {
                    transcriptText = segments.map((s: any) => s.text).join(' ');
                }

                finalTranscriptText = transcriptText;
                finalSegments = segments;
                updateItem(id, { subtitles: segments || null });
                addItemLog(id, `✓ ${segments?.length || 0} sous-titres générés`);

                // Gemini Analysis
                updateItem(id, { status: 'analyzing' });
                addItemLog(id, '💎 Analyse Gemini…');

                try {
                    const geminiRes = await callGeminiConstance({
                        model: 'gemini-3.5-flash',
                        contents: [{
                            role: 'user',
                            parts: [{ text: `Analyze this transcript in the context of LVMH. Identify key themes, luxury terminology, and sentiment. Transcript: ${transcriptText}` }],
                        }],
                    });
                    finalAnalysis = (geminiRes.data as any).candidates[0].content.parts[0].text;
                    updateItem(id, { analysis: finalAnalysis });
                    addItemLog(id, '✓ Analyse terminée');
                } catch (geminiErr: any) {
                    addItemLog(id, `⚠️ Gemini échoué (non-bloquant): ${geminiErr.message}`);
                }

                // If video is still uploading, notify user
                if (!finalVideoUrl) {
                    addItemLog(id, '⏳ En attente de la fin de l\'upload vidéo...');
                    updateItem(id, { status: 'uploading-video' }); // Revert status text to uploading video since audio is done
                }
            };

            // ─── Execute Branches in Parallel ───
            await Promise.all([videoUploadPromise(), audioPipelinePromise()]);

            // ─── 6. Create Firestore Project ───
            updateItem(id, { status: 'creating-project', progress: 100 });
            addItemLog(id, '📁 Création du projet dans Firestore…');

            const user = auth.currentUser;
            if (!user) throw new Error('Non authentifié — connectez-vous d\'abord');

            const projectId = uid();
            const fileName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension

            // Get video duration estimate from last subtitle segment
            const lastSeg = finalSegments?.[finalSegments.length - 1];
            const estimatedDuration = lastSeg?.end || 60;

            const videoClipId = uid();
            let transcriptUrl: string | undefined = undefined;

            if (finalTranscriptText) {
                addItemLog(id, '☁️ Upload du transcript texte vers R2…');
                const exportClient = getServerExportClient(user.uid);
                const tUrl = await exportClient.saveTranscript(projectId, videoClipId, finalTranscriptText);
                if (tUrl) {
                    transcriptUrl = tUrl;
                    addItemLog(id, '✓ Transcript sauvegardé');
                } else {
                    addItemLog(id, '⚠️ Échec de la sauvegarde du transcript');
                }
            }

            // Build the main video clip
            const videoClip: Clip = {
                id: videoClipId,
                name: fileName,
                type: 'video',
                trackId: 'v1',
                start: 0,
                duration: estimatedDuration,
                originalDuration: estimatedDuration,
                sourceDuration: estimatedDuration,
                trimStart: 0,
                url: finalVideoUrl || '',
                volume: 1,
                opacity: 1,
                fadeInDuration: 0,
                fadeOutDuration: 0,
                transform: { x: 0, y: 0, scale: 1, rotation: 0 },
                subtitles: finalSegments ? { fr: finalSegments.map((s, i) => ({ ...s, id: i })) } : undefined,
                transcriptUrl,
                audioSources: [{ id: 'original', type: 'original', url: finalVideoUrl || '' }],
                activeAudioSource: 'original',
            };

            const savedProject: SavedProject = {
                id: projectId,
                name: fileName,
                thumbnailUrl: '',
                clips: [videoClip],
                tracks: DEFAULT_TRACKS,
                montageVersions: [],
                exports: [],
                aiSuggestions: [],
                messages: finalAnalysis ? [
                    { id: 1, role: 'assistant', text: `🧠 **Analyse automatique:**\n\n${finalAnalysis}` }
                ] : [],
                createdAt: Date.now(),
                updatedAt: Date.now(),
            };

            await saveProjectToFirestore(savedProject);
            addItemLog(id, `✓ Projet "${fileName}" créé (${projectId})`);

            // Add to user's rootProjectIds in settings
            try {
                const settingsRef = firestore.collection('users').doc(user.uid).collection('settings').doc('layout');
                const settingsDoc = await settingsRef.get();
                const currentRootIds: string[] = settingsDoc.exists ? (settingsDoc.data()?.rootProjectIds || []) : [];

                if (!currentRootIds.includes(projectId)) {
                    await settingsRef.set({
                        rootProjectIds: [projectId, ...currentRootIds],
                        updatedAt: Date.now(),
                    }, { merge: true });
                }
                addItemLog(id, '✓ Projet ajouté à la sidebar');
            } catch (settingsErr: any) {
                addItemLog(id, `⚠️ Settings: ${settingsErr.message}`);
            }

            updateItem(id, { projectId });

            if (finalSegments && finalSegments.length > 0) {
                if (autoExport) {
                    await exportItemWithBurnedSubtitles(id);
                } else {
                    updateItem(id, { status: 'ready-export', progress: 100 });
                    addItemLog(id, '✍️ Sous-titres prêts: validez/éditez puis lancez l’export.');
                }
            } else {
                addItemLog(id, '⚠️ Aucun sous-titre → export ignoré');
                updateItem(id, { status: 'done', progress: 100 });
            }

        } catch (err: any) {
            console.error(`[Batch] ${file.name} failed:`, err);
            updateItem(id, { status: 'error', error: err.message });
            addItemLog(id, `❌ ${err.message}`);
        }
    }, [updateItem, addItemLog, autoExport, exportItemWithBurnedSubtitles, isFfmpegLoaded]);

    // ── Sequential queue runner ──
    const runQueue = useCallback(async () => {
        if (isProcessingRef.current) return;
        if (!isFfmpegLoaded) {
            addGlobalLog('⏳ File en pause: FFmpeg pas encore ONLINE.');
            return;
        }
        isProcessingRef.current = true;

        while (true) {
            const current = itemsRef.current;
            const next = current.find(i => i.status === 'queued');
            if (!next) break;
            addGlobalLog(`▶ Traitement: ${next.file.name}`);
            await processOneFile(next);
            addGlobalLog(`✓ Terminé: ${next.file.name}`);
        }

        addGlobalLog('🏁 Tous les fichiers traités !');
        isProcessingRef.current = false;
    }, [processOneFile, isFfmpegLoaded]);

    useEffect(() => {
        if (!isFfmpegLoaded) return;
        if (items.some(i => i.status === 'queued')) {
            runQueue();
        }
    }, [isFfmpegLoaded, items, runQueue]);

    // ── Drop handler ──
    const onDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.dataTransfer.files?.length) return;

        const newItems: BatchItem[] = [];
        for (let i = 0; i < e.dataTransfer.files.length; i++) {
            const f = e.dataTransfer.files[i];
            if (!f.type.startsWith('video/')) continue;
            newItems.push({
                id: `${Date.now()}_${i}_${Math.random().toString(36).slice(2, 8)}`,
                file: f,
                status: 'queued',
                progress: 0,
                r2VideoUrl: null,
                r2AudioUrl: null,
                subtitles: null,
                analysis: null,
                projectId: null,
                exportUrl: null,
                error: null,
                logs: [],
                localPreviewUrl: URL.createObjectURL(f),
            });
        }

        if (newItems.length === 0) return;

        addGlobalLog(`📂 ${newItems.length} vidéo(s) ajoutée(s) à la file`);
        setItems(prev => [...prev, ...newItems]);
        setSelectedItemId(prev => prev || newItems[0]?.id || null);
        setTimeout(() => runQueue(), 100);
    }, [runQueue]);

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const removeItem = (id: string) => {
        setItems(prev => {
            const target = prev.find(i => i.id === id);
            if (target) URL.revokeObjectURL(target.localPreviewUrl);
            const next = prev.filter(i => i.id !== id);
            if (selectedItemId === id) setSelectedItemId(next[0]?.id || null);
            return next;
        });
    };

    const selectedItem = items.find(i => i.id === selectedItemId) || null;

    // ── Stats ──
    const totalFiles = items.length;
    const doneFiles = items.filter(i => i.status === 'done').length;
    const errorFiles = items.filter(i => i.status === 'error').length;
    const activeFile = items.find(i => !['queued', 'ready-export', 'done', 'error'].includes(i.status));
    const updateSubtitleSegment = useCallback((itemId: string, index: number, patch: Partial<SubtitleSegment>) => {
        setItems(prev => prev.map(item => {
            if (item.id !== itemId || !item.subtitles) return item;
            const nextSubs = [...item.subtitles];
            nextSubs[index] = { ...nextSubs[index], ...patch };
            return { ...item, subtitles: normalizeSubtitleSegments(nextSubs) };
        }));
    }, []);

    const handleAddSubtitle = useCallback((itemId: string, afterIndex: number) => {
        setItems(prev => prev.map(item => {
            if (item.id !== itemId || !item.subtitles) return item;
            const ref = item.subtitles[Math.max(0, afterIndex)] || { start: 0, end: 1, text: '' };
            const newStart = ref.end;
            const newEnd = newStart + 1;
            const nextSubs = [...item.subtitles];
            nextSubs.splice(afterIndex + 1, 0, { text: 'New subtitle', start: newStart, end: newEnd });
            return { ...item, subtitles: normalizeSubtitleSegments(nextSubs) };
        }));
    }, []);

    const handleDeleteSubtitle = useCallback((itemId: string, index: number) => {
        setItems(prev => prev.map(item => {
            if (item.id !== itemId || !item.subtitles) return item;
            const nextSubs = item.subtitles.filter((_, i) => i !== index);
            return { ...item, subtitles: normalizeSubtitleSegments(nextSubs) };
        }));
    }, []);

    useEffect(() => {
        if (!selectedItem?.subtitles || selectedItem.subtitles.length === 0) {
            setActiveSubIndex(-1);
            return;
        }
        const idx = selectedItem.subtitles.findIndex(s => previewTime >= s.start && previewTime <= s.end);
        setActiveSubIndex(idx);
    }, [previewTime, selectedItem?.id, selectedItem?.subtitles]);

    useEffect(() => {
        if (activeSubIndex < 0 || !subListRef.current) return;
        const el = subListRef.current.children[activeSubIndex] as HTMLElement | undefined;
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [activeSubIndex]);

    useEffect(() => {
        if (selectedItem?.subtitles && selectedItem.subtitles.length > 0) {
            setShowSubEditor(true);
        }
    }, [selectedItem?.id, selectedItem?.subtitles]);

    // ── Admin: Firestore real-time listener for export jobs ──
    useEffect(() => {
        let unsubscribe: (() => void) | null = null;
        (async () => {
            try {
                const { collection, query, orderBy, limit, onSnapshot, getFirestore } = await import('firebase/firestore');
                const db = getFirestore();
                const jobsQuery = query(
                    collection(db, 'export-jobs'),
                    orderBy('createdAt', 'desc'),
                    limit(20)
                );
                unsubscribe = onSnapshot(jobsQuery, (snapshot) => {
                    const activeStatuses = ['processing', 'merging', 'queued', 'analyzing', 'downloading', 'encoding', 'uploading', 'pending'];
                    const jobs = snapshot.docs
                        .filter(doc => {
                            const id = doc.id;
                            if (/_chunk\d+$/.test(id)) return false;
                            if (id === '_global_lock') return false;
                            const data = doc.data();
                            if (data.type === 'burn-chunk') return false;
                            return true;
                        })
                        .map(doc => {
                            const data = doc.data();
                            const totalChunks = data.totalChunks || 0;
                            let chunksCompleted = 0;
                            let chunksFailed = 0;
                            for (let i = 0; i < totalChunks; i++) {
                                if (data[`chunk_${i}_status`] === 'complete') chunksCompleted++;
                                if (data[`chunk_${i}_status`] === 'failed') chunksFailed++;
                            }
                            return {
                                id: doc.id,
                                ...data,
                                status: data.status,
                                chunkCount: totalChunks,
                                chunksCompleted,
                                chunksFailed,
                            };
                        });
                    setAdminJobs(jobs);
                    setAdminActiveCount(jobs.filter(j => activeStatuses.includes(j.status)).length);
                    const lockDoc = jobs.find(j => activeStatuses.includes(j.status));
                    setAdminLock(lockDoc ? { lockedBy: lockDoc.id } : null);

                    // Resolve user display names (fire-and-forget)
                    const unknownUids = [...new Set(jobs.map((j: any) => j.userId).filter(Boolean))].filter(uid => !userNames[uid]);
                    if (unknownUids.length > 0) {
                        (async () => {
                            const { doc: fsDoc, getDoc, getFirestore: getFs } = await import('firebase/firestore');
                            const fsDb = getFs();
                            const nameMap: Record<string, string> = {};
                            await Promise.all(unknownUids.map(async (uid: string) => {
                                try {
                                    const userSnap = await getDoc(fsDoc(fsDb, 'users', uid));
                                    if (userSnap.exists()) {
                                        const d = userSnap.data();
                                        nameMap[uid] = d.displayName || d.name || d.firstName || uid.slice(0, 8);
                                    } else {
                                        nameMap[uid] = uid.slice(0, 8);
                                    }
                                } catch { nameMap[uid] = uid.slice(0, 8); }
                            }));
                            setUserNames(prev => ({ ...prev, ...nameMap }));
                        })();
                    }
                });
            } catch (err) {
                console.error('[AdminQueue] Firestore listener error:', err);
            }
        })();
        return () => { if (unsubscribe) unsubscribe(); };
    }, []);

    const cancelAdminJob = async (jobId: string) => {
        try {
            // Cancel via Firestore directly (avoids 429+CORS when server is busy)
            const { doc, getFirestore, updateDoc } = await import('firebase/firestore');
            const db = getFirestore();
            await updateDoc(doc(db, 'export-jobs', jobId), { status: 'cancelled', currentStep: 'Annulé par admin' });
            addGlobalLog(`❌ Job ${jobId} annulé`);
        } catch (err) { alert(`Erreur: ${err}`); }
    };

    const forceCancel = async () => {
        if (!confirm('Annuler TOUS les jobs actifs et libérer le lock ?')) return;
        try {
            const { collection, query, where, getDocs, updateDoc, getFirestore, doc, deleteDoc } = await import('firebase/firestore');
            const db = getFirestore();
            const activeStatuses = ['processing', 'merging', 'queued', 'analyzing', 'downloading', 'encoding', 'uploading', 'pending'];
            // Query active jobs and cancel them all via Firestore
            const jobsQuery = query(collection(db, 'export-jobs'), where('status', 'in', activeStatuses.slice(0, 10)));
            const snapshot = await getDocs(jobsQuery);
            let count = 0;
            for (const jobDoc of snapshot.docs) {
                await updateDoc(jobDoc.ref, { status: 'cancelled', currentStep: 'Force cancelled par admin' });
                count++;
            }

            // Actually delete the global lock doc
            await deleteDoc(doc(db, 'export-jobs', '_global_lock'));
            addGlobalLog(`⚠️ Force cancel: ${count} jobs annulés + Lock supprimé`);

            setAdminLock(null);
            setAdminActiveCount(0);
        } catch (err) { alert(`Erreur: ${err.message}`); }
    };

    const deleteAdminJob = async (jobId: string, skipConfirm = false) => {
        const job = adminJobs.find(j => j.id === jobId);
        const chunkCount = job?.chunkCount || 0;
        if (!skipConfirm && !confirm(`🗑️ Supprimer le job ${jobId} ?\n\n• Supprimer le doc Firestore\n• Tenter suppression ${chunkCount} chunks de R2\n• Cette action est irréversible`)) return;

        try {
            addGlobalLog(`🗑️ Suppression job ${jobId}...`);

            const { doc, getDoc, deleteDoc, getFirestore } = await import('firebase/firestore');
            const db = getFirestore();
            const jobDocRef = doc(db, 'export-jobs', jobId);

            // 1. Read job data before deleting (need chunk URLs for R2 cleanup)
            const jobSnap = await getDoc(jobDocRef);
            const data = jobSnap.exists() ? jobSnap.data() : null;

            // 2. Delete Firestore doc FIRST (instant, no 429 risk)
            await deleteDoc(jobDocRef);
            addGlobalLog(`✅ Job ${jobId} supprimé de Firestore`);

            // Close chunk manager if we were managing this job
            if (managedJobId === jobId) setManagedJobId(null);

            // 3. Try R2 cleanup in background (non-blocking, will fail silently if 429)
            if (data) {
                const totalChunks = data.totalChunks || 0;
                const R2_PUBLIC_URL = 'https://media.quicktexture.com';
                const keysToDelete: string[] = [];

                for (let i = 0; i < totalChunks; i++) {
                    const chunkUrl = data[`chunk_${i}_url`];
                    if (chunkUrl) {
                        const key = chunkUrl.replace(R2_PUBLIC_URL + '/', '').split('?')[0];
                        if (key) keysToDelete.push(key);
                    }
                }
                if (data.downloadUrl) {
                    const key = data.downloadUrl.replace(R2_PUBLIC_URL + '/', '').split('?')[0];
                    if (key) keysToDelete.push(key);
                }

                // Fire & forget — try to delete R2 files but don't block
                if (keysToDelete.length > 0) {
                    addGlobalLog(`🗑️ Nettoyage R2: ${keysToDelete.length} fichiers (en arrière-plan)...`);
                    Promise.allSettled(
                        keysToDelete.map(key =>
                            fetch(`${EXPORT_SERVICE_URL}/media/delete`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ key })
                            }).catch(() => { })
                        )
                    ).then(results => {
                        const ok = results.filter(r => r.status === 'fulfilled').length;
                        addGlobalLog(`🗑️ R2: ${ok}/${keysToDelete.length} fichiers supprimés ${ok < keysToDelete.length ? '(serveur occupé, re-tenter + tard)' : '✅'}`);
                    });
                }
            }

        } catch (err: any) {
            addGlobalLog(`❌ Delete failed: ${err.message}`);
            alert(`Erreur: ${err.message}`);
        }
    };

    const cancelAllReplicateJobs = async () => {
        const replicateJobs = adminJobs.filter(job => job.type === 'replicate-enhance' && (job.status === 'starting' || job.status === 'processing'));
        if (replicateJobs.length === 0) {
            alert("No active Replicate jobs to cancel.");
            return;
        }

        if (!window.confirm(`Are you sure you want to cancel all ${replicateJobs.length} active Replicate jobs?`)) {
            return;
        }

        addGlobalLog(`Cancelling ${replicateJobs.length} active Replicate jobs...`);
        let count = 0;
        for (const job of replicateJobs) {
            try {
                if (job.predictionId) {
                    await cancelReplicateJob({ predictionId: job.predictionId });
                    const db = firestore;
                    const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
                    if (auth.currentUser) {
                        const jobDocRef = doc(db, 'users', auth.currentUser.uid, 'predictions', job.id);
                        await updateDoc(jobDocRef, {
                            status: 'canceled',
                            updatedAt: serverTimestamp()
                        });
                    }
                    count++;
                }
            } catch (err: any) {
                console.error(`Failed to cancel job ${job.id}:`, err);
                addGlobalLog(`❌ Failed to cancel ${job.id}: ${err.message}`);
            }
        }
        addGlobalLog(`✅ Cancelled ${count}/${replicateJobs.length} jobs.`);
    };

    const deleteAllJobs = async () => {
        if (adminJobs.length === 0) {
            alert("Aucun job à supprimer.");
            return;
        }

        if (!confirm(`💣 DANGER : Supprimer DÉFINITIVEMENT TOUS les ${adminJobs.length} jobs (Actifs ET inactifs) ainsi que leurs fichiers R2 ?\n\nCette action va détruire la file d'attente complète !`)) return;

        addGlobalLog(`🧹 [WIPE] Destruction de ${adminJobs.length} jobs en cours...`);
        let count = 0;

        // 1. Force Cancel Active Jobs first via firestore updates
        if (adminActiveCount > 0) {
            await forceCancel();
        }

        // 2. Delete all docs and trigger R2 cleanup
        for (const job of adminJobs) {
            await deleteAdminJob(job.id, true);
            count++;
        }

        addGlobalLog(`✅ [WIPE] Terminé : ${count} jobs supprimés.`);
    };

    const activeStatuses = ['processing', 'merging', 'queued', 'analyzing', 'downloading', 'encoding', 'uploading', 'pending'];

    // ─── RENDER ──────────────────────────────────────────────────
    return (
        <div className="h-screen bg-white text-black font-mono border-8 border-black overflow-hidden flex flex-col">
            {/* Header */}
            <header className="border-b-4 border-black p-6 flex justify-between items-end shrink-0">
                <div>
                    <h1 className="text-4xl font-black uppercase tracking-tighter">
                        Batch Workflow
                    </h1>
                    <p className="text-gray-600 mt-1 uppercase text-xs tracking-widest">
                        Multi-Video — Extract, Transcribe, Create Project, Export
                    </p>
                </div>
                <div className="text-right">
                    <div className="text-xs font-bold uppercase">System Status</div>
                    <div className="text-sm">
                        FFmpeg: {isFfmpegLoaded
                            ? <span className="bg-black text-white px-1">ONLINE</span>
                            : <span className="bg-gray-200 px-1">LOADING</span>
                        }
                    </div>
                    {totalFiles > 0 && (
                        <div className="text-xs mt-1 font-bold">
                            {doneFiles}/{totalFiles} traités
                            {errorFiles > 0 && <span className="text-red-600 ml-2">{errorFiles} erreur(s)</span>}
                        </div>
                    )}
                </div>
            </header>

            <div className={`flex-1 overflow-y-auto p-8 ${showSubEditor && selectedItem?.subtitles?.length ? 'pr-[420px]' : ''}`}>
                <div className="max-w-5xl mx-auto space-y-8">

                    {/* Server Export Queue (Admin) */}
                    <div className="border-2 border-black">
                        <div
                            className="bg-black text-white px-3 py-2 text-xs font-bold uppercase flex items-center justify-between cursor-pointer"
                            onClick={() => setShowAdminPanel(p => !p)}
                        >
                            <div className="flex items-center gap-2">
                                <Server size={14} />
                                Server Export Queue
                                {adminActiveCount > 0 && (
                                    <span className="bg-yellow-500 text-black px-2 py-0.5 rounded text-[10px] font-black">
                                        {adminActiveCount} ACTIF{adminActiveCount > 1 ? 'S' : ''}
                                    </span>
                                )}
                                {adminLock?.lockedBy && (
                                    <span className="bg-red-600 px-2 py-0.5 rounded text-[10px] font-bold">
                                        🔒 LOCKED
                                    </span>
                                )}
                            </div>
                            <span className="text-[10px] opacity-50">{showAdminPanel ? '▼' : '▸'}</span>
                        </div>

                        {showAdminPanel && (
                            <div className="p-3 space-y-3 max-h-[600px] overflow-y-auto">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-black uppercase text-sm flex items-center gap-2">
                                        <Server size={14} />
                                        Admin Jobs ({adminActiveCount} actifs)
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        {adminJobs.filter(job => job.type === 'replicate-enhance' && (job.status === 'starting' || job.status === 'processing')).length > 0 && (
                                            <button
                                                onClick={cancelAllReplicateJobs}
                                                className="text-[10px] font-bold uppercase border border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600 px-2 py-1 transition-colors flex items-center gap-1"
                                                title="Annuler tous les jobs Replicate"
                                            >
                                                <XCircle size={10} />
                                                Cancel Replicate
                                            </button>
                                        )}
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const { doc, deleteDoc, getFirestore } = await import('firebase/firestore');
                                                    const db = getFirestore();
                                                    await deleteDoc(doc(db, 'export-jobs', '_global_lock'));
                                                    setAdminLock(null);
                                                    addGlobalLog('🔓 Lock supprimé — la file est débloquée');
                                                } catch (err: any) { alert(`Erreur: ${err.message}`); }
                                            }}
                                            className="text-[10px] border border-orange-500 text-orange-600 px-2 py-1 font-bold uppercase hover:bg-orange-500 hover:text-white transition-colors"
                                        >
                                            🔓 Débloquer la file
                                        </button>
                                        {adminActiveCount > 0 && (
                                            <button
                                                onClick={forceCancel}
                                                className="text-[10px] border border-red-500 text-red-500 px-2 py-1 font-bold uppercase hover:bg-red-500 hover:text-white transition-colors"
                                            >
                                                ⚠️ Force Cancel All + Unlock
                                            </button>
                                        )}
                                        {adminJobs.length > 0 && (
                                            <button
                                                onClick={deleteAllJobs}
                                                className="text-[10px] border border-red-600 text-red-600 px-2 py-1 font-bold uppercase hover:bg-red-600 hover:text-white transition-colors flex items-center gap-1"
                                                title="Supprimer TOUS les jobs (Actifs et inactifs) + Nettoyage R2"
                                            >
                                                <Trash2 size={12} />
                                                Delete All Mtfkr
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {adminJobs.length === 0 ? (
                                    <div className="text-xs text-gray-400 text-center py-4">Aucun job d'export</div>
                                ) : (
                                    adminJobs.map(job => {
                                        const isActive = activeStatuses.includes(job.status);
                                        const isFailed = job.status === 'failed' || job.status === 'cancelled';
                                        const isDone = job.status === 'complete' || job.status === 'completed';
                                        const isManaged = managedJobId === job.id;
                                        const borderColor = isActive ? 'border-yellow-400' :
                                            isFailed ? 'border-red-400' :
                                                isDone ? 'border-green-400' : 'border-gray-300';

                                        return (
                                            <div key={job.id} className={`border-2 ${borderColor} overflow-hidden`}>
                                                {/* Job header — click to expand/collapse */}
                                                <div
                                                    className={`flex items-center gap-3 p-2 text-xs cursor-pointer select-none ${isActive ? 'bg-yellow-50' : isFailed ? 'bg-red-50' : isDone ? 'bg-green-50' : 'bg-gray-50'
                                                        }`}
                                                    onClick={() => setManagedJobId(isManaged ? null : job.id)}
                                                >
                                                    {isActive && <Loader2 size={14} className="animate-spin text-yellow-600 shrink-0" />}
                                                    {isFailed && <AlertCircle size={14} className="text-red-500 shrink-0" />}
                                                    {isDone && <CheckCircle size={14} className="text-green-500 shrink-0" />}

                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-bold truncate">{job.id}</div>
                                                        <div className="text-[10px] text-gray-500">
                                                            {job.currentStep || job.status}
                                                            {job.userId && <span className="ml-2 opacity-50">user: {userNames[job.userId] || job.userId.slice(0, 8)}</span>}
                                                            {job.subtitleUrl && (
                                                                <a href={job.subtitleUrl} download target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline" onClick={e => e.stopPropagation()} title="Télécharger les sous-titres">
                                                                    🔤 <Download size={10} /> sous-titres
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="font-black text-sm tabular-nums shrink-0">
                                                        {job.chunksCompleted || 0}/{job.chunkCount}
                                                        {job.chunksFailed > 0 && <span className="text-red-500 ml-1">({job.chunksFailed}❌)</span>}
                                                    </div>

                                                    {isDone && job.downloadUrl && (
                                                        <a href={job.downloadUrl} download className="shrink-0" onClick={e => e.stopPropagation()}>
                                                            <Download size={14} className="text-green-600" />
                                                        </a>
                                                    )}

                                                    {isActive && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); cancelAdminJob(job.id); }}
                                                            className="shrink-0 text-red-500 hover:text-red-700"
                                                            title="Cancel job"
                                                        >
                                                            <XCircle size={16} />
                                                        </button>
                                                    )}

                                                    {/* Delete button */}
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); deleteAdminJob(job.id); }}
                                                        className="shrink-0 text-gray-400 hover:text-red-600 transition-colors"
                                                        title="Supprimer le job + fichiers R2"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>

                                                    <span className="text-[10px] opacity-40">{isManaged ? '▼' : '▸'}</span>
                                                </div>

                                                {/* Chunk Manager (auto-expand) */}
                                                {isManaged && managedChunks.length > 0 && (
                                                    <div className="p-3 bg-white border-t">
                                                        {/* Actions bar */}
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="flex items-center gap-2">
                                                                {managedChunks.some(c => ['failed', 'encoding', 'downloading'].includes(c.status)) && (
                                                                    <button
                                                                        onClick={() => retryFailedChunks(job.id)}
                                                                        className="text-[10px] bg-red-500 text-white px-2 py-1 font-bold uppercase hover:bg-red-600 transition-colors"
                                                                    >
                                                                        🔄 Retry {managedChunks.filter(c => ['failed', 'encoding', 'downloading'].includes(c.status)).length} Stuck
                                                                    </button>
                                                                )}
                                                                {managedChunks.filter(c => c.url).length > 0 && (
                                                                    <button
                                                                        onClick={concatManagedChunks}
                                                                        disabled={isConcatting}
                                                                        className={`text-[10px] font-bold uppercase px-2 py-1 border transition-colors ${isConcatting
                                                                            ? 'bg-gray-200 text-gray-500 border-gray-300 cursor-wait'
                                                                            : 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700'
                                                                            }`}
                                                                    >
                                                                        {isConcatting ? concatProgress : `🔗 Concat ${managedChunks.filter(c => c.url).length} chunks`}
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div className="flex gap-3 text-[9px] text-gray-400 font-bold uppercase">
                                                                <span>⏳ Attente</span>
                                                                <span>⚙️ Encode</span>
                                                                <span>✅ OK</span>
                                                                <span className="text-orange-500">⚠️ Récup</span>
                                                                <span className="text-red-500">❌ Fail</span>
                                                            </div>
                                                        </div>

                                                        {/* Concat result preview */}
                                                        {concatResultUrl && (
                                                            <div className="mt-3 border-2 border-green-500 bg-green-50 p-3">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <div className="font-black uppercase text-xs text-green-800">✅ Vidéo assemblée</div>
                                                                    <div className="flex items-center gap-2">
                                                                        <button
                                                                            onClick={() => {
                                                                                const a = document.createElement('a');
                                                                                a.href = concatResultUrl;
                                                                                a.download = concatFileName;
                                                                                a.click();
                                                                            }}
                                                                            className="text-[11px] font-bold uppercase px-3 py-1.5 bg-green-600 text-white border-2 border-green-600 hover:bg-green-700 transition-colors"
                                                                        >
                                                                            ⬇️ Télécharger {concatFileName}
                                                                        </button>
                                                                        <button
                                                                            onClick={() => { URL.revokeObjectURL(concatResultUrl); setConcatResultUrl(null); }}
                                                                            className="text-gray-400 hover:text-black"
                                                                        >
                                                                            <XCircle size={16} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                <video
                                                                    src={concatResultUrl}
                                                                    controls
                                                                    className="w-full max-h-[400px] bg-black rounded"
                                                                />
                                                            </div>
                                                        )}

                                                        {/* Chunk grid */}
                                                        <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-15 gap-1">
                                                            {managedChunks.map((chunk) => {
                                                                const hasUrl = !!chunk.url;
                                                                const isRecoverable = chunk.status === 'failed' && hasUrl;
                                                                const hasSubs = (chunk as any).hasSubtitles;
                                                                const icon = chunk.status === 'complete' ? '✅' :
                                                                    isRecoverable ? '⚠️' :
                                                                        chunk.status === 'encoding' ? '⚙️' :
                                                                            chunk.status === 'failed' ? '❌' :
                                                                                chunk.status === 'downloading' ? '⬇️' : '⏳';
                                                                const bg = chunk.status === 'complete' ? 'bg-green-100 border-green-400' :
                                                                    isRecoverable ? 'bg-orange-100 border-orange-400' :
                                                                        chunk.status === 'encoding' ? 'bg-blue-100 border-blue-400' :
                                                                            chunk.status === 'failed' ? 'bg-red-100 border-red-400' :
                                                                                'bg-gray-50 border-gray-200';

                                                                return (
                                                                    <div
                                                                        key={chunk.index}
                                                                        className={`relative border ${bg} p-1 text-center text-[10px] font-bold rounded-sm overflow-hidden ${hasUrl ? 'cursor-pointer hover:opacity-80 active:scale-95' : 'cursor-default'
                                                                            } transition-all group`}
                                                                        title={`Chunk ${chunk.index}\nStatus: ${chunk.status}\nSous-titres: ${hasSubs === true ? 'OUI ✅' : hasSubs === false ? 'NON ❌' : '?'}${chunk.error ? '\nError: ' + chunk.error : ''}${hasUrl ? '\nClick: view | Hover: download' : ''}`}
                                                                        onClick={() => {
                                                                            if (hasUrl) window.open(chunk.url!, '_blank');
                                                                        }}
                                                                    >
                                                                        <div className="leading-tight">{icon}</div>
                                                                        <div className="text-[8px] text-gray-500">{chunk.index}</div>
                                                                        {/* Subtitle indicator */}
                                                                        {hasSubs === true && (
                                                                            <div className="absolute top-0 left-0 text-[7px] leading-none" title="Sous-titres brûlés">🔤</div>
                                                                        )}
                                                                        {hasSubs === false && (
                                                                            <div className="absolute top-0 left-0 text-[7px] leading-none opacity-50" title="Sans sous-titres">🔇</div>
                                                                        )}
                                                                        {/* Download bar on hover */}
                                                                        {hasUrl && (
                                                                            <div
                                                                                className="absolute bottom-0 left-0 right-0 bg-black/80 text-white text-[7px] py-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer font-bold"
                                                                                title="Télécharger ce chunk"
                                                                                onClick={async (e) => {
                                                                                    e.stopPropagation();
                                                                                    try {
                                                                                        const resp = await fetch(chunk.url!);
                                                                                        const blob = await resp.blob();
                                                                                        const blobUrl = URL.createObjectURL(blob);
                                                                                        const a = document.createElement('a');
                                                                                        a.href = blobUrl;
                                                                                        a.download = `chunk_${String(chunk.index).padStart(3, '0')}.mp4`;
                                                                                        a.click();
                                                                                        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
                                                                                    } catch { window.open(chunk.url!, '_blank'); }
                                                                                }}
                                                                            >⬇️ DL</div>
                                                                        )}
                                                                        {chunk.status === 'encoding' && (
                                                                            <div className="absolute bottom-0 left-0 h-0.5 bg-blue-500 transition-all" style={{ width: `${chunk.progress}%` }} />
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>

                    {/* Drop Zone */}
                    <div
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        className={`
                            border-4 border-dashed rounded-none p-12 text-center transition-all cursor-pointer
                            ${items.length > 0 ? 'border-black bg-gray-50 p-6' : 'border-gray-300 hover:border-black hover:bg-gray-50'}
                        `}
                    >
                        <div className="flex flex-col items-center gap-3 opacity-50">
                            <CloudUpload size={items.length > 0 ? 28 : 48} strokeWidth={1} />
                            <div className={`font-bold uppercase ${items.length > 0 ? 'text-sm' : 'text-xl'}`}>
                                Drop Multiple Video Files Here
                            </div>
                            <div className="text-xs text-gray-400">MP4, MOV, WEBM — Traitement séquentiel automatique</div>
                        </div>
                    </div>

                    {/* Active Processing Banner */}
                    {activeFile && (
                        <div className="border-4 border-black bg-black text-white p-4 flex items-center gap-4">
                            <Loader2 size={24} className="animate-spin shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm truncate">{activeFile.file.name}</div>
                                <div className="text-xs opacity-80">{STATUS_LABELS[activeFile.status]}</div>
                            </div>
                            <div className="font-black text-2xl tabular-nums">{activeFile.progress}%</div>
                            <div className="w-32 h-2 bg-gray-700 relative">
                                <div
                                    className="absolute inset-y-0 left-0 bg-white transition-all duration-300"
                                    style={{ width: `${activeFile.progress}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Top Preview */}
                    {selectedItem && (
                        <div className="border-4 border-black bg-white">
                            <div className="p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-xs font-bold uppercase tracking-widest">
                                        Aperçu: {selectedItem.file.name}
                                    </div>
                                    <button
                                        onClick={() => setIsPreviewExpanded(prev => !prev)}
                                        className="text-xs border-2 border-black px-2 py-1 font-bold uppercase hover:bg-black hover:text-white transition-colors inline-flex items-center gap-1"
                                    >
                                        {isPreviewExpanded ? <Shrink size={13} /> : <Expand size={13} />}
                                        {isPreviewExpanded ? 'Réduire' : 'Agrandir'}
                                    </button>
                                </div>
                                <BatchVideoPreview
                                    item={selectedItem}
                                    expanded={isPreviewExpanded}
                                    onTimeChange={setPreviewTime}
                                    seekTo={previewSeekTime}
                                    onSeeked={() => setPreviewSeekTime(null)}
                                />
                            </div>
                        </div>
                    )}

                    {/* File Queue */}
                    {items.length > 0 && (
                        <div className="space-y-2">
                            <div className="font-bold uppercase text-xs tracking-widest border-b-2 border-black pb-1">
                                File Queue ({totalFiles})
                            </div>
                            {items.map((item) => (
                                <FileRow
                                    key={item.id}
                                    item={item}
                                    onRemove={removeItem}
                                    onSelect={setSelectedItemId}
                                    selected={item.id === selectedItemId}
                                    onExportNow={exportItemWithBurnedSubtitles}
                                    onOpenPreview={() => {
                                        setSelectedItemId(item.id);
                                        setIsPreviewExpanded(true);
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    {/* Global Logs */}
                    {globalLogs.length > 0 && (
                        <div className="border-2 border-black">
                            <div className="bg-black text-white px-3 py-1 text-xs font-bold uppercase">Console</div>
                            <div className="max-h-40 overflow-y-auto p-3 text-xs font-mono space-y-0.5">
                                {globalLogs.map((log, i) => (
                                    <div key={i} className="text-gray-600">{log}</div>
                                ))}
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* RIGHT SIDEBAR: Subtitle Editor (Constance-like) */}
            {showSubEditor && selectedItem?.subtitles && selectedItem.subtitles.length > 0 && (
                <div className="fixed right-0 top-[108px] bottom-0 w-[380px] border-l-4 border-black bg-gray-50 flex flex-col overflow-hidden z-10">
                    <div className="border-b-2 border-black p-4 bg-white">
                        <div className="flex items-center justify-between">
                            <h3 className="font-black uppercase text-sm flex items-center gap-2">
                                <Pencil size={14} />
                                Subtitle Editor ({selectedItem.subtitles.length})
                            </h3>
                            <div className="flex gap-2">
                                <label className="text-[10px] font-bold uppercase flex items-center gap-1 border border-gray-300 px-2 py-1">
                                    <input
                                        type="checkbox"
                                        checked={autoExport}
                                        onChange={(e) => setAutoExport(e.target.checked)}
                                    />
                                    Auto
                                </label>
                                <button
                                    onClick={() => exportItemWithBurnedSubtitles(selectedItem.id)}
                                    disabled={selectedItem.status === 'exporting'}
                                    className="text-xs font-bold bg-black text-white px-2 py-1 hover:bg-gray-800 disabled:opacity-50"
                                >
                                    Export
                                </button>
                                <button
                                    onClick={() => {
                                        if (!selectedItem.subtitles) return;
                                        const srtContent = generateSrt(selectedItem.subtitles);
                                        const blob = new Blob([srtContent], { type: 'text/srt' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `${selectedItem.file.name.replace(/\.[^/.]+$/, '')}.srt`;
                                        a.click();
                                        URL.revokeObjectURL(url);
                                    }}
                                    className="text-xs font-bold border border-black text-black px-2 py-1 hover:bg-gray-100 flex items-center gap-1"
                                    title="Télécharger .SRT"
                                >
                                    <Download size={12} /> SRT
                                </button>
                                {selectedItem.exportUrl && (
                                    <a
                                        href={selectedItem.exportUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs font-bold bg-gray-200 text-black px-2 py-1 hover:bg-gray-300"
                                    >
                                        DL
                                    </a>
                                )}
                                <button
                                    onClick={() => setShowSubEditor(false)}
                                    className="text-xs font-bold bg-gray-300 text-black px-2 py-1 hover:bg-gray-400"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="border-b border-gray-300 p-3 bg-white space-y-2">
                        <div className="flex items-center gap-1">
                            <Search size={12} className="text-gray-400 shrink-0" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search subtitles..."
                                className="flex-1 text-xs font-mono border border-gray-300 px-2 py-1 focus:outline-none focus:border-black"
                            />
                            <button
                                onClick={() => setShowReplace(!showReplace)}
                                className={`text-[10px] font-bold px-1.5 py-1 border ${showReplace ? 'bg-black text-white border-black' : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'}`}
                                title="Find & Replace"
                            >
                                <Replace size={10} />
                            </button>
                        </div>
                        {showReplace && searchQuery && (
                            <div className="flex items-center gap-1">
                                <span className="text-[10px] text-gray-400 shrink-0">→</span>
                                <input
                                    type="text"
                                    value={replaceQuery}
                                    onChange={(e) => setReplaceQuery(e.target.value)}
                                    placeholder="Replace with..."
                                    className="flex-1 text-xs font-mono border border-gray-300 px-2 py-1 focus:outline-none focus:border-black"
                                />
                                <button
                                    onClick={() => {
                                        if (!searchQuery || !selectedItem.subtitles) return;
                                        const regex = new RegExp(searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
                                        const updated = selectedItem.subtitles.map(s => ({ ...s, text: s.text.replace(regex, replaceQuery) }));
                                        setItems(prev => prev.map(i => i.id === selectedItem.id ? { ...i, subtitles: normalizeSubtitleSegments(updated) } : i));
                                        setSearchQuery('');
                                        setReplaceQuery('');
                                    }}
                                    className="text-[10px] font-bold bg-purple-600 text-white px-2 py-1 hover:bg-purple-700 shrink-0"
                                >
                                    Replace All
                                </button>
                            </div>
                        )}
                    </div>

                    <div ref={subListRef} className="flex-1 overflow-y-auto">
                        {selectedItem.subtitles
                            .map((seg, i) => ({ seg, i }))
                            .filter(({ seg }) => !searchQuery || seg.text.toLowerCase().includes(searchQuery.toLowerCase()))
                            .map(({ seg, i }) => (
                                <div
                                    key={`${selectedItem.id}-${i}`}
                                    className={`border-b border-gray-200 p-3 hover:bg-white transition-colors cursor-pointer ${activeSubIndex === i ? 'bg-green-50 border-l-4 border-l-green-500' : ''}`}
                                    onClick={() => setPreviewSeekTime(seg.start)}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase">#{i + 1}</span>
                                        <div className="flex items-center gap-1">
                                            <Clock size={10} className="text-gray-400" />
                                            <input
                                                type="number"
                                                step="0.1"
                                                value={seg.start.toFixed(1)}
                                                onChange={(e) => updateSubtitleSegment(selectedItem.id, i, { start: Number(e.target.value) || 0 })}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-14 text-[10px] font-mono bg-gray-100 border border-gray-300 px-1 py-0.5 text-center"
                                            />
                                            <span className="text-[10px] text-gray-400">→</span>
                                            <input
                                                type="number"
                                                step="0.1"
                                                value={seg.end.toFixed(1)}
                                                onChange={(e) => updateSubtitleSegment(selectedItem.id, i, { end: Number(e.target.value) || 0 })}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-14 text-[10px] font-mono bg-gray-100 border border-gray-300 px-1 py-0.5 text-center"
                                            />
                                        </div>
                                    </div>
                                    {editingSubId === i ? (
                                        <textarea
                                            value={seg.text}
                                            onChange={(e) => updateSubtitleSegment(selectedItem.id, i, { text: e.target.value })}
                                            onClick={(e) => e.stopPropagation()}
                                            onBlur={() => setEditingSubId(null)}
                                            autoFocus
                                            rows={2}
                                            className="w-full text-xs font-mono border-2 border-yellow-400 bg-yellow-50 p-1 resize-none focus:outline-none"
                                        />
                                    ) : (
                                        <div
                                            className="text-xs font-mono leading-relaxed hover:bg-gray-100 p-1 rounded cursor-text"
                                            onClick={(e) => { e.stopPropagation(); setEditingSubId(i); }}
                                        >
                                            {seg.text}
                                        </div>
                                    )}
                                    <div className="flex justify-end gap-1 mt-1">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleAddSubtitle(selectedItem.id, i); }}
                                            className="text-[10px] text-gray-400 hover:text-green-600 p-0.5"
                                            title="Add subtitle after"
                                        >
                                            <Plus size={10} />
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDeleteSubtitle(selectedItem.id, i); }}
                                            className="text-[10px] text-gray-400 hover:text-red-600 p-0.5"
                                            title="Delete subtitle"
                                        >
                                            <Trash2 size={10} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            )}

            {selectedItem?.subtitles && selectedItem.subtitles.length > 0 && !showSubEditor && (
                <button
                    onClick={() => setShowSubEditor(true)}
                    className="fixed right-4 top-1/2 -translate-y-1/2 bg-black text-white px-2 py-4 font-bold text-xs uppercase tracking-widest hover:bg-gray-800 border-2 border-black z-20"
                    style={{ writingMode: 'vertical-rl' }}
                >
                    <Pencil size={12} className="mb-2" /> Subtitles ({selectedItem.subtitles.length})
                </button>
            )}
        </div>
    );
};

const BatchVideoPreview: React.FC<{
    item: BatchItem;
    expanded: boolean;
    onTimeChange?: (t: number) => void;
    seekTo?: number | null;
    onSeeked?: () => void;
}> = ({ item, expanded, onTimeChange, seekTo, onSeeked }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [time, setTime] = useState(0);
    const source = item.r2VideoUrl || item.localPreviewUrl;
    const activeSubtitle = item.subtitles?.find((s) => time >= s.start && time <= s.end);

    useEffect(() => {
        if (seekTo == null || !videoRef.current) return;
        videoRef.current.currentTime = seekTo;
        onSeeked?.();
    }, [seekTo, onSeeked]);

    return (
        <div className={`relative border-2 border-black bg-black ${expanded ? 'h-[420px]' : 'h-[220px]'}`}>
            <video
                ref={videoRef}
                src={source}
                controls
                className="w-full h-full object-contain"
                onTimeUpdate={() => {
                    const current = videoRef.current?.currentTime || 0;
                    setTime(current);
                    onTimeChange?.(current);
                }}
            />
            {activeSubtitle?.text && (
                <div className="absolute left-0 right-0 bottom-3 flex justify-center pointer-events-none">
                    <div className="bg-black/75 text-white px-3 py-1 text-sm font-semibold max-w-[90%] text-center">
                        {activeSubtitle.text}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── FileRow sub-component ───────────────────────────────────────
const FileRow: React.FC<{
    item: BatchItem;
    onRemove: (id: string) => void;
    onSelect: (id: string) => void;
    selected: boolean;
    onExportNow: (id: string) => void;
    onOpenPreview: () => void;
}> = ({ item, onRemove, onSelect, selected, onExportNow, onOpenPreview }) => {
    const [expanded, setExpanded] = useState(false);

    const statusColor = {
        'queued': 'bg-gray-100',
        'extracting': 'bg-yellow-50 border-yellow-400',
        'uploading-video': 'bg-blue-50 border-blue-400',
        'uploading-audio': 'bg-blue-50 border-blue-400',
        'transcribing': 'bg-purple-50 border-purple-400',
        'analyzing': 'bg-indigo-50 border-indigo-400',
        'creating-project': 'bg-teal-50 border-teal-400',
        'ready-export': 'bg-amber-50 border-amber-400',
        'exporting': 'bg-orange-50 border-orange-400',
        'done': 'bg-green-50 border-green-400',
        'error': 'bg-red-50 border-red-400',
    }[item.status];

    const isActive = !['queued', 'done', 'error'].includes(item.status);
    const canRemove = item.status === 'queued' || item.status === 'done' || item.status === 'error';

    return (
        <div className={`border-2 border-black ${statusColor} transition-colors ${selected ? 'ring-2 ring-black' : ''}`}>
            <div
                className="flex items-center gap-3 px-3 py-2 cursor-pointer select-none"
                onClick={() => {
                    onSelect(item.id);
                    setExpanded(!expanded);
                }}
            >
                {/* Icon */}
                {item.status === 'done' && <CheckCircle size={16} className="text-green-600 shrink-0" />}
                {item.status === 'error' && <AlertCircle size={16} className="text-red-600 shrink-0" />}
                {isActive && <Loader2 size={16} className="animate-spin text-black shrink-0" />}
                {item.status === 'queued' && <FileVideo size={16} className="text-gray-400 shrink-0" />}

                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenPreview();
                    }}
                    className="shrink-0 border border-black bg-black"
                    title="Ouvrir en grand"
                >
                    <video
                        src={item.r2VideoUrl || item.localPreviewUrl}
                        className="w-24 h-14 object-cover"
                        muted
                        preload="metadata"
                    />
                </button>

                {/* Name */}
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{item.file.name}</div>
                    <div className="text-xs text-gray-500">
                        {(item.file.size / 1024 / 1024).toFixed(1)} MB
                        {item.projectId && <span className="ml-2 text-teal-600">📁 Projet créé</span>}
                    </div>
                </div>

                {/* Status */}
                <div className="text-xs font-mono shrink-0">{STATUS_LABELS[item.status]}</div>

                {/* Progress */}
                {isActive && (
                    <div className="w-20 text-right font-black text-sm tabular-nums">{item.progress}%</div>
                )}

                {/* Download export */}
                {item.exportUrl && (
                    <a
                        href={item.exportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-1 hover:bg-black hover:text-white transition-colors"
                        title="Télécharger l'export"
                    >
                        <Download size={14} />
                    </a>
                )}

                {(item.status === 'ready-export' || item.status === 'done') && item.subtitles && item.subtitles.length > 0 && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onExportNow(item.id); }}
                        className="text-xs font-bold px-2 py-1 border border-black hover:bg-black hover:text-white transition-colors"
                        title="Exporter avec sous-titres brûlés"
                    >
                        EXPORT
                    </button>
                )}

                {/* Open project link */}
                {item.projectId && (
                    <a
                        href={`/project/${item.projectId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs font-bold px-2 py-1 bg-black text-white hover:bg-gray-700 transition-colors"
                        title="Ouvrir le projet"
                    >
                        OPEN
                    </a>
                )}

                {/* Remove */}
                {canRemove && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onRemove(item.id); }}
                        className="p-1 hover:bg-black hover:text-white transition-colors"
                    >
                        <Trash2 size={14} />
                    </button>
                )}
            </div>

            {/* Progress bar */}
            {isActive && (
                <div className="h-1 bg-gray-200">
                    <div className="h-full bg-black transition-all duration-300" style={{ width: `${item.progress}%` }} />
                </div>
            )}

            {/* Expanded logs */}
            {expanded && item.logs.length > 0 && (
                <div className="border-t border-black px-3 py-2 max-h-32 overflow-y-auto">
                    {item.logs.map((log, i) => (
                        <div key={i} className="text-xs text-gray-500 font-mono">{log}</div>
                    ))}
                </div>
            )}

            {/* Error message */}
            {item.error && (
                <div className="border-t border-red-400 px-3 py-1 text-xs text-red-700 bg-red-50">
                    {item.error}
                </div>
            )}
        </div>
    );
};
